import { useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import { iaRa } from 'lib/helpers/utility';
import {
  CLEARINGHOUSE_STEP_FIELDS,
  ELIGIBILITY_STEP_FIELDS,
  hydrateStep
} from 'lib/constants/healthcareProgram/eligibilitySetup';

import Typography from 'components/shared/Typography/Typography';
import { currentPractice } from 'components/practice/practiceState';
import DragAndDropList from 'components/shared/DragAndDropList/DragAndDropList';

import EligibilityStepRow from './EligibilityStepRow';

const EligibilityStepsSection = ({ steps, onChange }) => {
  const practice = useRecoilValue(currentPractice);
  const isDental = practice?.kind === 'dental';

  // DnD/render needs the UI-only definition (label, description, pricing, isAi)
  // merged in. Dental practices don't get the alternate clearinghouse step —
  // their only provider is Stedi, so the cascade would just re-hit the same API.
  const hydratedSteps = useMemo(() => {
    return iaRa(steps)
      .filter((s) => !isDental || s.field !== ELIGIBILITY_STEP_FIELDS.ALTERNATE_CLEARINGHOUSE_AI)
      .map(hydrateStep);
  }, [steps, isDental]);

  // Coming-soon steps are pinned and not user-reorderable: they represent
  // capabilities not yet wired into the engine.
  const disabledItems = useMemo(
    () => hydratedSteps.filter((s) => s.comingSoon).map((s) => s.field),
    [hydratedSteps]
  );

  // alert_biller can't be the only enabled step (mirrors backend validators.js).
  const hasClearinghouseEnabled = useMemo(
    () => iaRa(steps).some((s) => CLEARINGHOUSE_STEP_FIELDS.includes(s.field) && s.enabled),
    [steps]
  );

  const handleStepUpdate = (field, patch) => {
    const next = (steps || []).map((s) => (s.field === field ? { ...s, ...patch } : s));

    // No clearinghouse left on → cascade alert_biller off (keep config to restore).
    const clearinghouseEnabled = next.some(
      (s) => CLEARINGHOUSE_STEP_FIELDS.includes(s.field) && s.enabled
    );
    const reconciled = clearinghouseEnabled
      ? next
      : next.map((s) =>
          s.field === ELIGIBILITY_STEP_FIELDS.ALERT_BILLER && s.enabled
            ? { ...s, enabled: false }
            : s
        );

    onChange(reconciled);
  };

  const handleReorder = (reordered) => {
    // DragAndDropList hands us hydrated items; strip the UI-only fields back
    // out before pushing into draft state so we only persist what the backend
    // schema accepts. Any step that's filtered out of the UI (e.g. dental's
    // alternate clearinghouse) is preserved at the end so it isn't lost.
    const reorderedFields = new Set(reordered.map((s) => s.field));
    const byField = new Map((steps || []).map((s) => [s.field, s]));
    const hiddenSteps = (steps || []).filter((s) => !reorderedFields.has(s.field));
    onChange([...reordered.map((s) => byField.get(s.field)), ...hiddenSteps]);
  };

  return (
    <section className="flex w-full flex-col gap-3">
      <Typography size="large" weight="medium">
        Eligibility setup
      </Typography>
      <Typography size="small" color="neutral-700">
        Drag to reorder the steps Myriad will follow when verifying patient eligibility. Enable or
        disable each step for your practice.
      </Typography>

      <DragAndDropList
        items={hydratedSteps}
        onDragEnd={handleReorder}
        disabledItems={disabledItems}
        droppableId="healthcare-program-eligibility-steps"
        parentClassName="flex flex-col gap-2 mt-2"
        renderItem={(step, index, setDisableDragging) => (
          <EligibilityStepRow
            step={step}
            index={index}
            setDisableDragging={setDisableDragging}
            disableToggle={
              step.field === ELIGIBILITY_STEP_FIELDS.ALERT_BILLER &&
              !step.enabled &&
              !hasClearinghouseEnabled
            }
            onUpdate={(patch) => handleStepUpdate(step.field, patch)}
          />
        )}
      />
    </section>
  );
};

export default EligibilityStepsSection;
